import Swiper from 'swiper';
import { Thumbs, Pagination, Navigation, Zoom } from 'swiper/modules';
import $ from 'jquery';
import 'swiper/css/bundle';

class ProductGallery extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.thumbSlider = null;
  }

  /**
   * Ensures the thumbnail slider hasn't scrolled past valid bounds.
   * Swiper's Thumbs module calls slideTo() which positions slides at the START of view,
   * causing whitespace when scrolling to the last few slides.
   * This corrects the position by clamping to maxTranslate.
   */
  correctThumbPosition() {
    if (!this.thumbSlider) return;

    const thumbSlider = this.thumbSlider;
    const currentTranslate = thumbSlider.getTranslate();
    const maxTranslate = thumbSlider.maxTranslate(); // Most negative (furthest scroll)
    const minTranslate = thumbSlider.minTranslate(); // 0 (no scroll)

    // DEBUG: Log translate values
    console.log('[ThumbSlider Debug]', {
      currentTranslate,
      maxTranslate,
      minTranslate,
      activeIndex: this.swiper?.activeIndex,
      totalSlides: thumbSlider.slides?.length,
      containerHeight: thumbSlider.el?.offsetHeight,
    });

    // Clamp translate to valid bounds
    let correctedTranslate = currentTranslate;

    if (currentTranslate < maxTranslate) {
      // Over-scrolled past end - this creates whitespace
      console.log('[ThumbSlider] Correcting over-scroll:', currentTranslate, '->', maxTranslate);
      correctedTranslate = maxTranslate;
    } else if (currentTranslate > minTranslate) {
      // Over-scrolled past start
      console.log('[ThumbSlider] Correcting under-scroll:', currentTranslate, '->', minTranslate);
      correctedTranslate = minTranslate;
    }

    if (correctedTranslate !== currentTranslate) {
      thumbSlider.setTranslate(correctedTranslate);
      thumbSlider.updateProgress();
      thumbSlider.updateSlidesClasses();
    }
  }

  /**
   * Sets up LQIP image load handlers to clear placeholder backgrounds
   */
  setupLqipHandlers() {
    const lqipSlides = this.querySelectorAll('.lqip-slide');
    lqipSlides.forEach((slide) => {
      const img = slide.querySelector('img');
      if (!img) return;

      const clearLqip = () => {
        slide.classList.add('lqip-loaded');
        slide.style.backgroundImage = 'none';
      };

      if (img.complete && img.naturalWidth > 0) {
        clearLqip();
      } else {
        img.addEventListener('load', clearLqip, { once: true });
      }
    });
  }

  connectedCallback() {
    // Set up LQIP handlers first
    this.setupLqipHandlers();

    let ThumbnailSlider = null;

    if (this.querySelector('thumbnail-slider')) {
      ThumbnailSlider = new Swiper(this.querySelector('thumbnail-slider'), {
        loop: false,
        slidesPerView: 'auto',
        direction: 'vertical',
        observeParents: true,
        observer: true,
        watchOverflow: true,
        watchSlidesProgress: true,
      });
      this.thumbSlider = ThumbnailSlider;
    }

    this.swiper = new Swiper(this.querySelector('.swiper-product-gallery'), {
      loop: false,
      zoom: true,
      autoHeight: true,
      spaceBetween: 10,
      grabCursor: true,
      slidesPerView: 1,
      observeParents: true,
      allowTouchMove: this.getAttribute('turn_slides') !== 'false',
      observer: true,
      modules: [Thumbs, Pagination, Navigation, Zoom],
      navigation: {
        nextEl: '.swipe-button-next',
        prevEl: '.swipe-button-prev',
      },
      pagination: {
        el: '.product-gallery-pagination',
        clickable: true,
      },
      thumbs: {
        swiper: ThumbnailSlider,
      },
      zoom: {
        maxRatio: 3,
        minRatio: 1,
      },
      on: {
        init: () => {
          // Unhide slides after Swiper is initialized
          const hiddenSlides = this.querySelectorAll(
            '.swiper-slide[class*="!hidden"]'
          );
          hiddenSlides.forEach((slide) => {
            slide.classList.remove('!hidden');
          });

          // Initialize zoom contrast for the first slide
          this.updateZoomContrast();
        },
        slideChange: () => {
          // After Thumbs module positions the thumbnail, correct it to prevent whitespace
          // Use setTimeout to run after Thumbs module's slideTo completes
          setTimeout(() => {
            this.correctThumbPosition();
          }, 0);
        },
      },
    });
    this.querySelector('.zoom-img').addEventListener(
      'click',
      function () {
        $(this).toggleClass('active');
        if ($(this).hasClass('active')) {
          this.swiper.zoom.in();
        } else {
          this.swiper.zoom.out();
        }
      }.bind(this)
    );

    this.swiper.on(
      'slideChange',
      function () {
        const video =
          this.swiper.slides[this.swiper.activeIndex].querySelector('video');

        if (video != null) {
          video.currentTime = 0;
          video.play();
        }

        // Update zoom button contrast when slide changes
        this.updateZoomContrast();
      }.bind(this)
    );

    document.addEventListener(
      'changeSlider',
      function (e) {
        let mediaIDs = [];
        this.swiper.slides.forEach((slider) => {
          mediaIDs.push(slider.getAttribute('data-media-id'));
        });
        const url = e.detail.media_id.toString();

        const protocolRelativeUrl = url.replace(/^https?:/, '');
        const targetIndex = mediaIDs.indexOf(protocolRelativeUrl);

        // Navigate main slider - this will trigger slideChange which handles thumb positioning
        this.swiper.slideTo(
          targetIndex,
          e.detail.transition_speed,
          false
        );
      }.bind(this)
    );
  }

  /**
   * Analyzes the current slide's image to determine the average background color
   * and updates the zoom button contrast accordingly
   */
  getAverageColor(image) {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      // Sample a small area for performance
      canvas.width = 10;
      canvas.height = 10;

      image.onload = () => {
        ctx.drawImage(image, 0, 0, 10, 10);
        const imageData = ctx.getImageData(0, 0, 10, 10);
        const data = imageData.data;

        let r = 0,
          g = 0,
          b = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
        }

        const pixelCount = data.length / 4;
        resolve({
          r: Math.round(r / pixelCount),
          g: Math.round(g / pixelCount),
          b: Math.round(b / pixelCount),
        });
      };

      // Handle already loaded images
      if (image.complete) {
        image.onload();
      }
    });
  }

  /**
   * Updates the zoom button contrast based on the current slide's background
   * Uses standard luminance calculation to determine if background is light or dark
   */
  updateZoomContrast() {
    const activeSlide = this.querySelector('.swiper-slide-active');
    if (!activeSlide) return;

    const img = activeSlide.querySelector('img');
    if (!img) return;

    this.getAverageColor(img)
      .then((color) => {
        // Calculate relative luminance using standard formula
        const brightness =
          (color.r * 0.299 + color.g * 0.587 + color.b * 0.114) / 255;

        // Set contrast color: black for light backgrounds, white for dark backgrounds
        const contrastColor = brightness > 0.5 ? 'black' : 'white';
        this.style.setProperty('--zoom-contrast-color', contrastColor);
      })
      .catch((error) => {
        console.log('Could not analyze image color for zoom contrast:', error);
      });
  }
}

customElements.define('product-gallery', ProductGallery);
